import React, { Suspense, useState, useEffect, useRef } from 'react';
import { Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuthStore } from '../../stores/authStore';
import { useOrgContextStore } from '../../stores/orgContextStore';
import { orgBasePath } from '../../lib/orgPath';
import { isMultiOrgRole, canManageUsers, canRunImports } from '../../lib/permissions';
import { Button } from '../../components/ui/Button';
import { orgPath } from '../../lib/orgPath';
import { useTicketsEnabled, useForecastEnabled, useFaceSearchEnabled } from '../../lib/features';
import TicketBadge from '../../components/tickets/TicketBadge';
import api from '../../lib/api';
import organizationService from '../../services/organizationService';
import { useFeatureRefresh } from '../../lib/useFeatureRefresh';
// Pages are code-split: each route loads its own chunk on first visit instead of one 3 MB bundle.
const ForecastPage = React.lazy(() => import('../production/ForecastPage'));
const TicketsPage = React.lazy(() => import('../tickets/TicketsPage'));
const OrganizationModelsListPage = React.lazy(() => import('./OrganizationModelsListPage'));
const OrganizationModelsGridPage = React.lazy(() => import('./OrganizationModelsGridPage'));
const OrganizationModelsSplitView = React.lazy(() => import('./OrganizationModelsSplitView'));
const ModelDuplicatesPage = React.lazy(() => import('./ModelDuplicatesPage'));
const FaceSearchPage = React.lazy(() => import('./FaceSearchPage'));
const AttributeSuggestionsPage = React.lazy(() => import('./AttributeSuggestionsPage'));
const ModelDetailPage = React.lazy(() => import('./ModelDetailPage'));
const AgenciesListPage = React.lazy(() => import('./AgenciesListPage'));
const CreateUserPage = React.lazy(() => import('./CreateUserPage'));
const UsersListPage = React.lazy(() => import('./UsersListPage'));
const OrganizationUserDetailPage = React.lazy(() => import('./OrganizationUserDetailPage'));
const OrganizationAgencyDetailPage = React.lazy(() => import('./OrganizationAgencyDetailPage'));
const OrganizationUserEditPage = React.lazy(() => import('./OrganizationUserEditPage'));
const OrganizationAgencyEditPage = React.lazy(() => import('./OrganizationAgencyEditPage'));
const CreateModelBookPage = React.lazy(() => import('./CreateModelBookPage'));
const ModelBooksListPage = React.lazy(() => import('./ModelBooksListPage'));
const OrganizationClientsPage = React.lazy(() => import('./OrganizationClientsPage'));
const ClientListPage = React.lazy(() => import('./ClientListPage'));
const OrganizationClientModelAssignmentPage = React.lazy(() => import('./OrganizationClientModelAssignmentPage'));
const OrganizationClientModelAssignmentGridPage = React.lazy(() => import('./OrganizationClientModelAssignmentGridPage'));
const OrganizationClientAdminAssignmentPage = React.lazy(() => import('./OrganizationClientAdminAssignmentPage'));
const ScoutDashboard = React.lazy(() => import('../scout/ScoutDashboard'));
const CreateModelPage = React.lazy(() => import('../scout/CreateModelPage'));
const ModelEditPage = React.lazy(() => import('./ModelEditPage'));
const ModelBookViewer = React.lazy(() => import('../../components/modelbook/ModelBookViewer'));
const EnhancedModelBookEditor = React.lazy(() => import('../../components/modelbook/EnhancedModelBookEditor'));
const ModelApprovalDashboard = React.lazy(() => import('./ModelApprovalDashboard'));
const UserProfilePage = React.lazy(() => import('../user/UserProfilePage'));
const PSSImportPage = React.lazy(() => import('./PSSImportPage'));
const PSSImportCastingsPage = React.lazy(() => import('./PSSImportCastingsPage'));
const PSSImportBookingsPage = React.lazy(() => import('./PSSImportBookingsPage'));
const PSSImportProductionPage = React.lazy(() => import('./PSSImportProductionPage'));
const PSSImportFinancePage = React.lazy(() => import('./PSSImportFinancePage'));
const PSSImportContactsPage = React.lazy(() => import('./PSSImportContactsPage'));
const MediaslideImportPage = React.lazy(() => import('./MediaslideImportPage'));
const CastingListPage = React.lazy(() => import('../casting/CastingListPage'));
const CastingDetailPage = React.lazy(() => import('../casting/CastingDetailPage'));
const ProductionListPage = React.lazy(() => import('../production/ProductionListPage'));
const ProductionLocationsPage = React.lazy(() => import('../production/ProductionLocationsPage'));
const CostcentersPage = React.lazy(() => import('../production/CostcentersPage'));
const ConfirmationTemplatesPage = React.lazy(() => import('../production/ConfirmationTemplatesPage'));
const TimesheetListPage = React.lazy(() => import('../timesheet/TimesheetListPage'));
const ProductionDetailPage = React.lazy(() => import('../production/ProductionDetailPage'));
const BookingsListPage = React.lazy(() => import('../booking/BookingsListPage'));
const InvoiceListPage = React.lazy(() => import('../finance/InvoiceListPage'));
const BillingOverviewPage = React.lazy(() => import('../finance/BillingOverviewPage'));
const CallTimesPage = React.lazy(() => import('../production/CallTimesPage'));
const InvoiceDetailPage = React.lazy(() => import('../finance/InvoiceDetailPage'));

// Import organization-specific pages
import CabosLogo from '../../assets/cabos-logo.svg';
import UserIcon from '../../assets/user-icon.svg';
import Footer from '../../components/layout/Footer';

/**
 * Gate for this shell's Users pages (/organization-admin/users/*, /<slug>/users/*).
 *
 * Two things have to be decided before those pages render:
 *
 *  - multi_org_viewer / finance_admin must not reach them at all. Both are read-only outside
 *    comments and billing, and every user endpoint refuses them, so they go back to the
 *    dashboard of the mount they are on rather than being shown a screen full of 403s.
 *  - multi_org_admin has user management, but not *here*. These pages talk to
 *    /api/organizations/users*, which needs a real organization_admin session — a multi-org
 *    role only has one via the X-Organization-Id morph, and even then that endpoint refuses
 *    every role outside pool_admin/scout/agency/model and cannot write UserOrganizationAccess
 *    rows at all. Its Users section is /all/users, on /api/admin/users*, which is what the
 *    backend allowlisted for it — so forward there, keeping the rest of the path.
 *
 * Real organization_admins (and admin/super_admin browsing an org) fall through unchanged.
 */
const OrgUsersRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuthStore();
  const { pathname, search } = useLocation();

  if (user && !canManageUsers(user.role)) {
    return <Navigate to={orgBasePath()} replace />;
  }
  if (user && isMultiOrgRole(user.role)) {
    const at = pathname.indexOf('/users');
    const suffix = at >= 0 ? pathname.slice(at + '/users'.length) : '';
    return <Navigate to={`/all/users${suffix}${search}`} replace />;
  }
  return <>{children}</>;
};

interface Model {
  id: number;
  first_name: string;
  last_name: string;
  model_name?: string;
  status: string;
  contacts?: Array<{
    type: string;
    value: string;
  }> | any; // Made more flexible
  measurements?: any;
  media_uploads?: Array<{
    id: number;
    file_path: string;
    media_type: string;
    upload_date: string;
  }>;
}

interface Pool {
  id: number;
  name: string;
  description?: string;
  is_active: boolean;
  max_models?: number;
  _count: {
    model_assignments: number;
  };
  model_assignments: Array<{
    id: number;
    model: Model;
  }>;
}

interface Organization {
  id: number;
  name: string;
  description?: string;
  website?: string;
  is_active: boolean;
  pools: Pool[];
  organization_admins: Array<{
    id: number;
    first_name?: string;
    last_name?: string;
    email: string;
  }>;
  pool_admin_assignments: Array<{
    id: number;
    user: {
      id: number;
      first_name?: string;
      last_name?: string;
      email: string;
    };
  }>;
  stats: {
    total_users: number;
    organization_admins: number;
    pool_admins: number;
    total_pools: number;
    active_pools: number;
    total_models: number;
  };
}

// Shared styling for the top nav buttons/links.
const navBtnClass =
  'bg-white border border-black px-4 py-2 font-bold text-sm text-black hover:bg-black hover:text-white hover:border-white flex items-center justify-center';

// A single top-level nav link.
const NavItem: React.FC<{ to: string; label: string; active: boolean; badge?: React.ReactNode }> = ({ to, label, active, badge }) => (
  <Link to={to} className={`${navBtnClass} ${active ? 'bg-gray-100' : ''}`}>
    <div className="text-center">
      <div className="inline-flex items-center">{label}{badge}</div>
    </div>
  </Link>
);

// A top-level nav dropdown with a list of links. Manages its own open state.
const NavDropdown: React.FC<{
  label: string;
  active: boolean;
  items: Array<{ to: string; label: string }>;
  alignRight?: boolean;
}> = ({ label, active, items, alignRight = false }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className={`${navBtnClass} ${active ? 'bg-gray-100' : ''}`}
      >
        <div className="text-center flex items-center gap-1">
          <div>{label}</div>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={open ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'} />
          </svg>
        </div>
      </button>
      {open && (
        <div className={`absolute ${alignRight ? 'right-0' : 'left-0'} top-full mt-1 z-50 min-w-[180px] bg-white border border-black shadow-lg py-1`}>
          {items.map((it) => (
            <Link
              key={it.to}
              to={it.to}
              className="block px-4 py-2 text-sm font-medium text-black hover:bg-black hover:text-white"
              onClick={() => setOpen(false)}
            >
              {it.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const OrganizationAdminDashboard: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { activeOrg } = useOrgContextStore();
  // Org switcher: every org the user may enter (multi_org_admin/super_admin see several).
  const [myOrgs, setMyOrgs] = useState<Array<{ id: number; name: string; slug: string | null }>>([]);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreatePool, setShowCreatePool] = useState(false);
  const [poolFormData, setPoolFormData] = useState({
    name: '',
    description: '',
    max_models: ''
  });
  const [submitting, setSubmitting] = useState(false);
  // Dashboard agenda: active counts + every booking today → +7 days.
  const [agenda, setAgenda] = useState<{
    active_castings: number;
    active_productions: number;
    bookings: Array<{
      id: number; status: string; call_time: string | null; calltime_studio: string | null;
      date: string | null; location: string | null; model_name: string; model_id?: number;
      production_id?: number; production_name?: string; client_name?: string | null;
    }>;
  } | null>(null);
  // Counts are still fetched (setters used below) but no longer displayed as cards.
  const [, setAgencyCount] = useState<number>(0);
  const [, setModelCount] = useState<number | null>(null);
  const [, setClientCount] = useState<number | null>(null);
  // Auto-hide header on scroll (more room for the content). Collapses when scrolling down.
  const [headerHidden, setHeaderHidden] = useState(false);
  const lastScrollRef = useRef(0);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const headerAnimatingRef = useRef(false);
  const headerAnimTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navRef = useRef<HTMLElement | null>(null);
  const headerHeightRef = useRef(64); // last measured expanded height

  // Collapsing the header makes the scroll container below it taller, which shrinks its
  // max scrollTop — so at the bottom of a page the browser clamps scrollTop downwards.
  // That clamp is indistinguishable from the user scrolling up, and used to flip the
  // header straight back open, growing the container again: an endless open/close
  // flicker at the bottom of every screen. Three guards:
  //  1. only collapse when there is enough scrollable room that collapsing cannot use it
  //     all up — otherwise the content ends up fitting exactly, no further scroll event
  //     can ever fire, and the header (with the whole nav) is stuck hidden;
  //  2. ignore scroll events while the height is animating, since geometry is in flux;
  //  3. never re-open off a scroll-up that leaves us pinned to the bottom — that is the
  //     clamp, not a gesture.
  const HEADER_ANIM_MS = 300; // keep in sync with duration-300 on the <nav> below
  const setHeader = (hidden: boolean) => {
    if (headerHidden === hidden) return;
    headerAnimatingRef.current = true;
    if (headerAnimTimerRef.current) clearTimeout(headerAnimTimerRef.current);
    headerAnimTimerRef.current = setTimeout(() => {
      headerAnimatingRef.current = false;
      // Re-baseline: scrollTop may have been clamped while the height changed.
      if (contentRef.current) lastScrollRef.current = contentRef.current.scrollTop;
    }, HEADER_ANIM_MS);
    setHeaderHidden(hidden);
  };
  const onContentScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const y = el.scrollTop;
    if (headerAnimatingRef.current) return; // height in flux — scroll geometry unreliable
    const last = lastScrollRef.current;
    const room = el.scrollHeight - el.clientHeight;
    const atBottom = y >= room - 2;
    if (!headerHidden && navRef.current) {
      headerHeightRef.current = navRef.current.getBoundingClientRect().height || headerHeightRef.current;
    }
    // Collapsing eats headerHeight of scrollable room. Require a healthy margin beyond
    // that, so what is left is enough for an unhurried scroll-up to register and bring
    // the header back — with only a few px left the whole gesture finishes inside
    // HEADER_ANIM_MS and gets swallowed by the lock above, stranding the header hidden.
    const canHide = room > headerHeightRef.current + 120;
    if (y > last && y > 72 && canHide) setHeader(true);
    else if (y < last - 4 && !atBottom) setHeader(false);
    lastScrollRef.current = y;
  };
  useEffect(() => () => {
    if (headerAnimTimerRef.current) clearTimeout(headerAnimTimerRef.current);
  }, []);
  // New screen → show the nav again, and drop any in-flight animation lock.
  useEffect(() => {
    if (headerAnimTimerRef.current) clearTimeout(headerAnimTimerRef.current);
    headerAnimatingRef.current = false;
    lastScrollRef.current = 0;
    setHeaderHidden(false);
  }, [location.pathname]);

  const isDashboardHome = location.pathname === orgPath('/organization-admin') || location.pathname === orgPath('/organization-admin/');
  const path = location.pathname;
  const isPssImportActive =
    path === orgPath('/organization-admin/pss-import') ||
    path.startsWith(orgPath('/organization-admin/pss-import-castings')) ||
    path.startsWith(orgPath('/organization-admin/pss-import-bookings')) ||
    path.startsWith(orgPath('/organization-admin/pss-import-production')) ||
    path.startsWith(orgPath('/organization-admin/pss-import-finance')) ||
    path.startsWith(orgPath('/organization-admin/pss-import-contacts'));
  // Grouped nav active states
  const isModelsActive =
    path.startsWith(orgPath('/organization-admin/models')) || path.startsWith(orgPath('/organization-admin/scouting')) ||
    path.startsWith(orgPath('/organization-admin/face-search')) || path.startsWith(orgPath('/organization-admin/attribute-suggestions'));
  const isClientsActive =
    path.startsWith(orgPath('/organization-admin/pools')) || path.startsWith(orgPath('/organization-admin/client-admins'));
  const isAgenciesActive = path.startsWith(orgPath('/organization-admin/agencies'));
  const isTicketsActive = path.startsWith(orgPath('/organization-admin/tickets'));
  const ticketsOn = useTicketsEnabled();
  const forecastOn = useForecastEnabled();
  const faceSearchOn = useFaceSearchEnabled();
  useFeatureRefresh();
  const isProductionActive =
    path.startsWith(orgPath('/organization-admin/productions')) ||
    path.startsWith(orgPath('/organization-admin/castings')) ||
    path.startsWith(orgPath('/organization-admin/billing')) ||
    path.startsWith(orgPath('/organization-admin/call-times')) ||
    path.startsWith(orgPath('/organization-admin/forecast'));
  const isImportActive = isPssImportActive || path === orgPath('/organization-admin/mediaslide-import');

  useEffect(() => {
    if (isDashboardHome) {
      loadOrganizationData();
    }
  }, [isDashboardHome]);

  // Load the org switcher options once.
  useEffect(() => {
    api.get('/organizations/my-organizations')
      .then((res) => setMyOrgs(res.data?.data?.organizations ?? []))
      .catch(() => setMyOrgs([]));
  }, []);

  const switchableOrgs = myOrgs.filter((o) => o.slug);
  const currentOrgSlug = activeOrg?.slug ?? switchableOrgs.find((o) => o.id === user?.organization_id)?.slug ?? '';
  // multi_org_admin / super_admin can jump back to the merged "All organizations" view.
  // The read-only multi-org roles browse the same way, so they get it too.
  // A multi-org role with a single organization gets no "All organizations" entry: that view
  // would only show this one org again, minus the organization context some pages need.
  const canSeeAllMode =
    (isMultiOrgRole(user?.role) && switchableOrgs.length > 1) || user?.role === 'super_admin' || user?.role === 'admin';
  const switchOrg = (slug: string) => {
    if (!slug || slug === currentOrgSlug) return;
    const subPath = location.pathname.slice(orgBasePath().length) || '/';
    // Detail pages carry ids from the previous org — jump to the section list instead.
    const section = subPath.split('/').filter(Boolean)[0];
    if (slug === '__all') {
      const allSections = ['productions', 'castings', 'call-times', 'billing', 'agencies', 'client-admins'];
      navigate(`/all/${section && allSections.includes(section) ? section : 'productions'}`);
      return;
    }
    navigate(`/${slug}${section ? `/${section}` : ''}`);
  };

  const loadOrganizationData = async () => {
    try {
      setLoading(true);
      const [orgResponse, agendaRes, agenciesRes, modelsRes, clientsRes] = await Promise.allSettled([
        api.get('/organizations/my-organization'),
        api.get('/organizations/dashboard-agenda'),
        // Only the count is shown: the light reply carries id, name and a model count per agency.
        organizationService.getAgencies({ light: 1 }),
        api.get('/organizations/models?limit=1'),
        api.get('/organizations/available-pool-admins'),
      ]);

      if (orgResponse.status === 'fulfilled') {
        const data = orgResponse.value.data;
        setOrganization(data.data.organization);
      }

      if (agendaRes.status === 'fulfilled') {
        const d = (agendaRes.value as any).data?.data;
        if (d) setAgenda(d);
      }

      if (agenciesRes.status === 'fulfilled') {
        // organizationService.getAgencies returns response.data = { success, data: { agencies: [...] } }
        const d = agenciesRes.value as any;
        setAgencyCount(d?.data?.agencies?.length ?? 0);
      }

      if (modelsRes.status === 'fulfilled') {
        // /organizations/models returns { data: { pagination: { total } } }
        const d = (modelsRes.value as any).data?.data;
        setModelCount(d?.pagination?.total ?? null);
      }

      if (clientsRes.status === 'fulfilled') {
        // /organizations/available-pool-admins returns { data: { pool_admins: [...] } }
        const d = (clientsRes.value as any).data?.data;
        setClientCount(d?.pool_admins?.length ?? null);
      }
    } catch (error: any) {
      console.error('Error loading organization data:', error);
      toast.error(error.message || 'Failed to load organization data');
    } finally {
      setLoading(false);
    }
  };

  const createPool = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://app.cabos.mondula.com/api'}/pools`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...poolFormData,
          max_models: poolFormData.max_models ? parseInt(poolFormData.max_models) : null
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create pool');
      }

      toast.success('Pool created successfully!');
      setShowCreatePool(false);
      setPoolFormData({ name: '', description: '', max_models: '' });
      loadOrganizationData(); // Reload to show the new pool
    } catch (error: any) {
      console.error('Error creating pool:', error);
      toast.error(error.message || 'Failed to create pool');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && isDashboardHome) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading organization data...</p>
        </div>
      </div>
    );
  }

  if (!organization && isDashboardHome) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Organization not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-100 flex flex-col overflow-hidden">
      {/* Header — compact + auto-hides on scroll */}
      <nav
        ref={navRef}
        className={`bg-black shadow flex-shrink-0 transition-[max-height] duration-300 ${
          headerHidden ? 'max-h-0 overflow-hidden' : 'max-h-24 overflow-visible'
        }`}
      >
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Link to={orgPath('/organization-admin')}>
                <img
                  src={CabosLogo}
                  alt="Cabos Logo"
                  className="h-8 w-auto cursor-pointer"
                />
              </Link>
              {switchableOrgs.length > 1 ? (
                <select
                  value={currentOrgSlug}
                  onChange={(e) => switchOrg(e.target.value)}
                  className="bg-black text-white border border-gray-600 text-sm font-semibold px-2 py-1 cursor-pointer hover:border-white focus:outline-none"
                  title="Switch organization"
                >
                  {!currentOrgSlug && <option value="">Select organization…</option>}
                  {canSeeAllMode && <option value="__all">All organizations</option>}
                  {switchableOrgs.map((o) => (
                    <option key={o.id} value={o.slug ?? ''}>{o.name}</option>
                  ))}
                </select>
              ) : activeOrg ? (
                <span className="text-sm font-semibold text-white border border-gray-600 px-2 py-1">{activeOrg.name}</span>
              ) : null}
            </div>
            <div className="flex items-center">
              <span className="text-sm text-white">Welcome, </span>
              <Link to={orgPath('/organization-admin/profile')}>
                <button style={{ cursor: "pointer", padding: "2px 5px 5px", backgroundColor: "rgb(18, 18, 18)", border: "none", margin: "0px", height: "24px", fontSize: "18px", fontWeight: 900 }}>
                  <img src={UserIcon} alt="User" width="20" height="auto" />
                </button>
              </Link>
              <Link to={orgPath('/organization-admin/profile')} className="text-sm text-white hover:text-gray-300 hover:underline">
                {user?.email}
              </Link>
              <button style={{ cursor: "pointer", padding: 5, backgroundColor: "#121212", border: "none", margin: 0, height: "24px", paddingTop: 2, fontSize: 18, fontWeight: 900 }} onClick={logout}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 198.4 198.5" width="20px" height="20px">
                <path d="m197.8,102.2c.2-.7.4-1.4.4-2.1,0-.3.2-.5.2-.8s-.1-.5-.2-.8c-.1-.7-.2-1.4-.4-2.1-.2-.6-.4-1.2-.7-1.7-.2-.3-.2-.6-.4-.9l-19.8-29.8c-.2-.3-.4-.6-.6-.9-3.3-4.4-9.5-5.3-13.9-2-4.4,3.3-5.3,9.5-2,13.9l9.6,14.4h-60.9c-5.5,0-9.9,4.4-9.9,9.9s4.4,9.9,9.9,9.9h60.9l-9.6,14.4c-1.1,1.6-1.6,3.5-1.6,5.4,0,5.5,4.4,9.9,9.9,10,3.3,0,6.4-1.6,8.2-4.4l19.8-29.8v-.1c.2-.3.3-.6.4-.9.4-.6.6-1.1.7-1.6Z" fill="#ffffff" stroke-width="0"/>
                <path d="m138.9,125.9c-5.5,0-9.9,4.4-9.9,9.9v32.8c0,5.5-4.4,9.9-9.9,9.9H29.8c-5.5,0-9.9-4.4-9.9-9.9V29.8c0-5.5,4.4-9.9,9.9-9.9h89.3c5.5,0,9.9,4.4,9.9,9.9v27.9c0,5.5,4.4,9.9,9.9,9.9s9.9-4.4,9.9-9.9v-27.9c0-16.4-13.3-29.8-29.8-29.8H29.8C13.3,0,0,13.3,0,29.8v138.9c0,16.4,13.3,29.8,29.8,29.8h89.3c16.4,0,29.8-13.3,29.8-29.8v-32.8c-.1-5.6-4.5-10-10-10Z" fill="#ffffff" stroke-width="0"/></svg>
              </button>
              <div className='bg-black flex items-center'>
            {/* <Link
              to={orgPath('/organization-admin')}
              className={`bg-white border border-black px-4 py-2 font-bold text-sm text-black hover:bg-black hover:text-white hover:border-white flex items-center justify-center ${
                isDashboardHome
                  ? 'bg-gray-100'
                  : ''
              }`}
            >
              <div className="text-center">
                <div>Dashboard</div>
              </div>
            </Link> */}
            <NavDropdown
              label="Models"
              active={isModelsActive}
              items={[
                { to: orgPath('/organization-admin/models'), label: 'Models' },
                { to: orgPath('/organization-admin/scouting'), label: 'Scouting' },
                { to: orgPath('/organization-admin/models/duplicates'), label: 'Find duplicates' },
                ...(faceSearchOn
                  ? [
                      { to: orgPath('/organization-admin/face-search'), label: 'Face search' },
                      { to: orgPath('/organization-admin/attribute-suggestions'), label: 'AI suggestions' },
                    ]
                  : []),
              ]}
            />
            <NavItem to={orgPath('/organization-admin/client-admins')} label="Clients" active={isClientsActive} />
            <NavItem to={orgPath('/organization-admin/agencies')} label="Agencies" active={isAgenciesActive} />
            {ticketsOn && <NavItem to={orgPath('/organization-admin/tickets')} label="Tickets" active={isTicketsActive} badge={<TicketBadge />} />}
            <NavDropdown
              label="Production"
              active={isProductionActive}
              items={[
                { to: orgPath('/organization-admin/productions'), label: 'Productions' },
                { to: orgPath('/organization-admin/castings'), label: 'Castings' },
                { to: orgPath('/organization-admin/billing'), label: 'Billing' },
                { to: orgPath('/organization-admin/call-times'), label: 'Call Times' },
                ...(forecastOn ? [{ to: orgPath('/organization-admin/forecast'), label: 'Forecast' }] : []),
                { to: orgPath('/organization-admin/production-locations'), label: 'Locations' },
                { to: orgPath('/organization-admin/costcenters'), label: 'Costcenters' },
                { to: orgPath('/organization-admin/confirmation-templates'), label: 'Confirmation Templates' },
              ]}
            />
            {canRunImports(user?.role) && (
            <NavDropdown
              label="Import"
              active={isImportActive}
              alignRight
              items={[
                { to: orgPath('/organization-admin/mediaslide-import'), label: 'Mediaslide Import' },
                { to: orgPath('/organization-admin/pss-import'), label: 'PSS Model Import' },
                { to: orgPath('/organization-admin/pss-import-castings'), label: 'PSS Casting Import' },
                { to: orgPath('/organization-admin/pss-import-bookings'), label: 'PSS Booking Import' },
                { to: orgPath('/organization-admin/pss-import-production'), label: 'PSS Production Import' },
                { to: orgPath('/organization-admin/pss-import-finance'), label: 'PSS Finance Import' },
                { to: orgPath('/organization-admin/pss-import-contacts'), label: 'PSS Contacts Import' },
              ]}
            />
            )}
            {/* <Link
              to={orgPath('/organization-admin/timesheets')}
              className={`bg-white border border-black px-4 py-2 font-bold text-sm text-black hover:bg-black hover:text-white hover:border-white flex items-center justify-center ${
                location.pathname.startsWith(orgPath('/organization-admin/timesheets'))
                  ? 'bg-gray-100'
                  : ''
              }`}
            >
              <div className="text-center">
                <div>Timesheets</div>
              </div>
            </Link> */}
            {/* <Link
              to={orgPath('/organization-admin/invoices')}
              className={`bg-white border border-black px-4 py-2 font-bold text-sm text-black hover:bg-black hover:text-white hover:border-white flex items-center justify-center ${
                location.pathname.startsWith(orgPath('/organization-admin/invoices'))
                  ? 'bg-gray-100'
                  : ''
              }`}
            >
              <div className="text-center">
                <div>Invoices</div>
              </div>
            </Link> */}
          {/*   <Link
              to={orgPath('/organization-admin/model-books')} 
              className={`bg-white border border-black px-4 py-2 font-bold text-sm text-black hover:bg-black hover:text-white hover:border-white flex items-center justify-center ${
                location.pathname.startsWith(orgPath('/organization-admin/model-books')) 
                  ? 'bg-gray-100' 
                  : ''
              }`}
            >
              <div className="text-center">
                <div>Model Books</div>
              </div>
            </Link>*/}
             {/*        <Link 
              to={orgPath('/organization-admin/users')} 
              className={`bg-white border border-black px-4 py-2 font-bold text-sm text-black hover:bg-black hover:text-white hover:border-white flex items-center justify-center ${
                location.pathname.startsWith(orgPath('/organization-admin/users')) 
                  ? 'bg-gray-100' 
                  : ''
              }`}
            >
              <div className="text-center">
                <div>Team</div>
              </div>
            </Link>
      <Link 
              to={orgPath('/organization-admin/change-requests')} 
              className={`bg-white border border-black px-4 py-2 font-bold text-sm text-black hover:bg-black hover:text-white hover:border-white flex items-center justify-center ${
                location.pathname.startsWith(orgPath('/organization-admin/change-requests')) 
                  ? 'bg-gray-100' 
                  : ''
              }`}
            >
            <div className="text-center">
             TODO: Add Change Requests<div>Change Requests</div> 
              </div>
            </Link> */}
            </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div ref={contentRef} className="flex-1 overflow-y-auto" onScroll={onContentScroll}>
      <Suspense fallback={<div className="flex items-center justify-center py-24"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" /></div>}>
      <Routes>
        <Route path="/" element={
          <div className="max-w-7xl mx-auto px-6 py-8">

            {/* Call times + This week — what actually happens on set */}
            {(() => {
              const todayIso = new Date().toISOString().slice(0, 10);
              const bookings = agenda?.bookings ?? [];
              const weekBookings = bookings.filter((b) => b.date && b.date > todayIso);

              const groupByProduction = (list: typeof bookings) => {
                const map = new Map<number, { name: string; client: string | null; items: typeof bookings }>();
                for (const b of list) {
                  const key = b.production_id ?? 0;
                  const g = map.get(key) ?? { name: b.production_name ?? '—', client: b.client_name ?? null, items: [] as typeof bookings };
                  g.items.push(b);
                  map.set(key, g);
                }
                return [...map.entries()];
              };
              const fmtDay = (iso: string) => {
                const d = new Date(iso + 'T00:00:00');
                return d.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit' });
              };
              const weekDates = [...new Set(weekBookings.map((b) => b.date as string))].sort();

              return (
                <div className="space-y-6">
                  {/* Call times — the same display as /call-times, embedded. Confirmed-only
                      by default, so it replaces the old "Today on set" section (T1). */}
                  <div className="bg-white border border-gray-100 shadow-sm px-6 py-4">
                    <CallTimesPage embedded />
                  </div>

                  {/* This week */}
                  <div className="bg-white border border-gray-100 shadow-sm">
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                      <h2 className="text-sm font-semibold uppercase tracking-widest text-gray-500">This week</h2>
                      <span className="text-xs text-gray-400">next 7 days</span>
                    </div>
                    {weekDates.length === 0 ? (
                      <p className="px-6 py-8 text-sm text-gray-400">No shoot days in the next 7 days.</p>
                    ) : (
                      <div className="divide-y divide-gray-50 max-h-[480px] overflow-y-auto">
                        {weekDates.map((date) => (
                          <div key={date} className="px-6 py-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">{fmtDay(date)}</p>
                            {groupByProduction(weekBookings.filter((b) => b.date === date)).map(([pid, g]) => (
                              <Link
                                key={pid}
                                to={orgPath(`/organization-admin/productions/${pid}`)}
                                className="flex items-center justify-between py-1 hover:bg-gray-50 transition-colors"
                              >
                                <span className="text-sm text-gray-900 truncate">{g.name}</span>
                                <span className="text-xs text-gray-400 whitespace-nowrap ml-2">
                                  {g.items.length} model{g.items.length !== 1 ? 's' : ''}
                                  {g.client ? ` · ${g.client}` : ''}
                                </span>
                              </Link>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

          </div>
        } />
        
        {/* All the routes for different sections */}
        <Route path="/models" element={<OrganizationModelsSplitView />} />
        <Route path="/models/duplicates" element={<ModelDuplicatesPage />} />
        {faceSearchOn && <Route path="/face-search" element={<FaceSearchPage />} />}
        {faceSearchOn && <Route path="/attribute-suggestions" element={<AttributeSuggestionsPage />} />}
        <Route path="/models/grid" element={<OrganizationModelsGridPage />} />
        <Route path="/models/list" element={<OrganizationModelsListPage />} />
        <Route path="/models/:id" element={<ModelDetailPage />} />
        <Route path="/models/:id/edit" element={<ModelEditPage />} />
        <Route path="/pools" element={<OrganizationClientsPage />} />
        <Route path="/pools/:poolId/models" element={<OrganizationClientModelAssignmentGridPage />} />
        <Route path="/pools/:poolId/models/list" element={<OrganizationClientModelAssignmentPage />} />
        <Route path="/pools/:poolId/admins" element={<OrganizationClientAdminAssignmentPage />} />
        <Route path="/agencies" element={<AgenciesListPage />} />
        <Route path="/agencies/new" element={<OrganizationAgencyEditPage />} />
        <Route path="/agencies/:agencyId" element={<OrganizationAgencyDetailPage />} />
        <Route path="/agencies/:agencyId/edit" element={<OrganizationAgencyEditPage />} />
        <Route path="/users" element={<OrgUsersRoute><UsersListPage /></OrgUsersRoute>} />
        <Route path="/users/create" element={<OrgUsersRoute><CreateUserPage /></OrgUsersRoute>} />
        <Route path="/users/:userId" element={<OrgUsersRoute><OrganizationUserDetailPage /></OrgUsersRoute>} />
        <Route path="/users/:userId/edit" element={<OrgUsersRoute><OrganizationUserEditPage /></OrgUsersRoute>} />
        <Route path="/model-books" element={<ModelBooksListPage />} />
        <Route path="/model-books/create" element={<CreateModelBookPage />} />
        <Route path="/model-books/:id" element={<ModelBookViewer />} />
        <Route path="/model-books/:id/edit" element={<EnhancedModelBookEditor />} />
        <Route path="/client-admins" element={<ClientListPage />} />
        <Route path="/scouting/*" element={<ScoutDashboard />} />
        <Route path="/scouting/create-model" element={<CreateModelPage />} />
        <Route path="/change-requests" element={<ModelApprovalDashboard />} />
        {/* Imports: gated on the role, not only hidden from the nav above. This shell also serves
            the /:orgSlug mount, which the read-only multi-org roles do browse — so an ungated route
            here would leave every import screen reachable by URL for exactly the roles the client
            asked to hide it from, rendering an upload form whose submit can only 403. */}
        {canRunImports(user?.role) && (
          <>
            <Route path="/pss-import" element={<PSSImportPage />} />
            <Route path="/pss-import-castings" element={<PSSImportCastingsPage />} />
            <Route path="/pss-import-bookings" element={<PSSImportBookingsPage />} />
            <Route path="/pss-import-production" element={<PSSImportProductionPage />} />
            <Route path="/pss-import-finance" element={<PSSImportFinancePage />} />
            <Route path="/pss-import-contacts" element={<PSSImportContactsPage />} />
            <Route path="/mediaslide-import" element={<MediaslideImportPage />} />
          </>
        )}
        <Route path="/castings" element={<CastingListPage />} />
        <Route path="/castings/:id" element={<CastingDetailPage />} />
        <Route path="/bookings" element={<BookingsListPage />} />
        <Route path="/productions" element={<ProductionListPage />} />
        {ticketsOn && <Route path="/tickets" element={<TicketsPage />} />}
        <Route path="/production-locations" element={<ProductionLocationsPage />} />
        <Route path="/costcenters" element={<CostcentersPage />} />
        <Route path="/confirmation-templates" element={<ConfirmationTemplatesPage />} />
        <Route path="/productions/:id" element={<ProductionDetailPage />} />
        <Route path="/timesheets" element={<TimesheetListPage />} />
        <Route path="/invoices" element={<InvoiceListPage />} />
        <Route path="/billing" element={<BillingOverviewPage />} />
        <Route path="/call-times" element={<CallTimesPage />} />
        {forecastOn && <Route path="/forecast" element={<ForecastPage />} />}
        {/* Old route — measurements is now a view toggle on the Call Times screen */}
        <Route path="/call-times-measurements" element={<Navigate to={orgPath('/organization-admin/call-times') + '?view=measurements'} replace />} />
        <Route path="/invoices/:id" element={<InvoiceDetailPage />} />
        <Route path="/profile" element={<UserProfilePage />} />
      </Routes>
      </Suspense>
      </div>
      {/* Footer — compact bar below the scroll area; reserves its own space so it never overlaps content */}
      <Footer />

      {/* Create Pool Modal */}
      {showCreatePool && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white  p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Create New Model List</h2>
            <form onSubmit={createPool} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Model List Name *
                </label>
                <input
                  type="text"
                  required
                  value={poolFormData.name}
                  onChange={(e) => setPoolFormData({ ...poolFormData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300  focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter model list name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={poolFormData.description}
                  onChange={(e) => setPoolFormData({ ...poolFormData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300  focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter model list description"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Models (optional)
                </label>
                <input
                  type="number"
                  value={poolFormData.max_models}
                  onChange={(e) => setPoolFormData({ ...poolFormData, max_models: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300  focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Leave empty for unlimited"
                  min="1"
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowCreatePool(false);
                    setPoolFormData({ name: '', description: '', max_models: '' });
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="flex-1"
                >
                  {submitting ? 'Creating...' : 'Create Model List'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default OrganizationAdminDashboard; 